import axios, { AxiosResponse, AxiosRequestConfig } from "axios";

import {
  correlationHeaders,
  syncCorrelationIdFromAxiosHeaders,
} from "@/utils/correlationId";

interface RetryAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

type FailedRequest = {
  resolve: (token?: string | null) => void;
  reject: (err: any) => void;
};

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_BASE_URL,
  withCredentials: true,
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  config.headers = config.headers ?? {};
  if (token) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  Object.assign(config.headers, correlationHeaders());
  return config;
});

let isRefreshing = false;
let failedQueue: FailedRequest[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

apiClient.interceptors.response.use(
  (response) => {
    syncCorrelationIdFromAxiosHeaders(response.headers);
    return response;
  },
  async (error) => {
    if (error.response?.headers) {
      syncCorrelationIdFromAxiosHeaders(error.response.headers);
    }
    const originalRequest = error.config as RetryAxiosRequestConfig;

    if (originalRequest.url?.includes("/token/refresh")) {
      console.log('Rejecting here', originalRequest.url);
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          if (token && originalRequest.headers) {
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
          }
          return apiClient(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem("refresh_token");
      if (!refreshToken) {
        window.location.href = "/login";
        return Promise.reject(error);
      }

      try {
        const { data } = await axios.post(
          `${import.meta.env.VITE_BASE_URL}/token/refresh`,
          null,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${refreshToken}`,
              "Cache-Control": "no-cache",
              ...correlationHeaders(),
            },
            withCredentials: true,
          }
        );

        const newToken = data.token;

        if (newToken) {
          localStorage.setItem("token", newToken);
        }

        processQueue(null, newToken ?? null);

        if (newToken && originalRequest.headers) {
          originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
        }

        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        console.log('Refresh error: ', refreshError);
        localStorage.clear();
        window.location.href = "/login";
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export const apiRequest = async <T>(
  url: string,
  method: "GET" | "POST" | "PUT" | "DELETE",
  data?: any,
  params?: Record<string, any>
): Promise<T> => {
  const isFormData = typeof data !== "undefined" && data instanceof FormData;
  const response: AxiosResponse<T> = await apiClient({
    method,
    url,
    data,
    params,
    headers: isFormData ? {} : { "Content-Type": "application/json" },
  });
  return response.data;
};

export default apiClient;
